import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { useQuery } from '@tanstack/react-query';
import {
  AccessUser,
  CompanyInvoiceWithRelations,
  GigWithRelations,
  Job,
  PerDiemWithRelations,
  permissions,
  UserRole,
  WithPermission,
  WithRestriction,
} from '@utilities-one/api-sdk';
import { Spinner } from '@utilities-one/gpm-ui';

import { routeAccess } from '@/config/auth/routeAccess';
import { canBypassCompanyInvoiceRoleScopeForSplicing } from '@/entities/companyInvoice/utils/canBypassCompanyInvoiceRoleScopeForSplicing';
import { isSplicingInvoice } from '@/entities/companyInvoice/utils/invoiceType';
import { SessionUpdate } from '@/entities/user/components/SessionUpdate';
import { AccessContext } from '@/entities/user/providers/accessContext';
import { userAccessUserQueryOptions, userSessionUserQueryOptions } from '@/entities/user/query/user.queries';
import * as access from '@/entities/user/utils/access';
import { hasPermission } from '@/entities/user/utils/access';

// The first `getSessionUser` right after a post-login redirect can transiently
// come back empty — the just-issued auth cookie may not be applied on the
// immediately-redirected request yet, or the just-created session row isn't
// readable for a few hundred ms (read-after-write). `getSessionUser` resolves
// such failures to `null` (it does not throw), so React Query's `retry` never
// engages. Retry the session read a bounded number of times before treating the
// user as logged-out — a `refetch()` here is exactly what the manual page
// refresh users currently rely on does. Mirrors the retry-once guard already in
// SessionUpdate. (UG-4367)
const SESSION_RETRY_LIMIT = 2;
const SESSION_RETRY_DELAY_MS = 300;

/** The slice of a project definition the VP scope reads — whatever else the caller's shape carries. */
type RegionOwnedDefinition = {
  state?: { market?: { division?: { owners?: { id: string }[] | null } | null } | null } | null;
};

export const AccessProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<AccessUser | null>(null);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const sessionRetryRef = useRef(0);

  // Public routes that don't require authentication
  const isPublicRoute = pathname === '/' || pathname.startsWith('/share/');

  const sessionQuery = useQuery({
    ...userSessionUserQueryOptions(),
    enabled: !isPublicRoute,
  });

  const sessionData = sessionQuery.data;

  const accessUserQuery = useQuery({
    ...userAccessUserQueryOptions({
      userId: sessionData?.userId ?? '',
      sessionId: sessionData?.sessionId ?? '',
    }),
    enabled: !isPublicRoute && !!sessionData?.userId && !!sessionData?.sessionId,
  });

  const matchRoute = (path: string, routePattern: string, exact = false) => {
    const regexPattern = new RegExp(
      '^' +
        routePattern
          .replace(/\[.*?\]/g, '[^/]+') // Replace `[id]` with `[^/]+` (matches any segment)
          .replace(/\//g, '\\/') + // Escape `/` for regex
        (exact ? '' : '(?:/.*)?') + // Allow deeper routes unless the rule is exact
        '$'
    );
    return regexPattern.test(path);
  };

  const isAccessDenied = useMemo(
    () =>
      routeAccess.some(({ pathname: routePath, permission, restriction, exact }) => {
        if (!matchRoute(pathname, routePath, exact) || !user) return false;

        const lacksPermission =
          permission && !hasPermission(user, ...(Array.isArray(permission) ? permission : [permission]));
        const failsRestriction = restriction && !restriction(user);

        return lacksPermission || failsRestriction;
      }),
    [pathname, user]
  );

  // A successful session read clears the retry budget so a later failure gets
  // its own fresh set of attempts.
  useEffect(() => {
    if (isPublicRoute || sessionQuery.data != null) {
      sessionRetryRef.current = 0;
    }
  }, [isPublicRoute, sessionQuery.data]);

  useEffect(() => {
    if (isPublicRoute || sessionQuery.isPending || sessionQuery.isFetching) return;

    const sessionFailed = sessionQuery.isError || sessionQuery.data == null;
    if (!sessionFailed) return;

    // Tolerate a transient empty/failed read before bouncing to login (UG-4367).
    if (sessionRetryRef.current < SESSION_RETRY_LIMIT) {
      sessionRetryRef.current += 1;
      const timer = setTimeout(() => {
        void sessionQuery.refetch();
      }, SESSION_RETRY_DELAY_MS);
      return () => clearTimeout(timer);
    }

    if (sessionQuery.isError) {
      console.error('Failed to load session:', sessionQuery.error);
    }
    Sentry.setUser(null);
    setIsRedirecting(true);
    navigate('/');
  }, [
    isPublicRoute,
    navigate,
    sessionQuery.data,
    sessionQuery.error,
    sessionQuery.isError,
    sessionQuery.isPending,
    sessionQuery.isFetching,
    sessionQuery.refetch,
  ]);

  useEffect(() => {
    if (accessUserQuery.data) {
      setUser(accessUserQuery.data);
      Sentry.setUser({
        id: accessUserQuery.data.id,
        email: accessUserQuery.data.email,
        username: accessUserQuery.data.name,
      });
      Sentry.setTag('role', accessUserQuery.data.role?.name ?? 'unknown');
      Sentry.setTag('company_id', accessUserQuery.data.companyId ?? 'none');
    }
  }, [accessUserQuery.data]);

  // Allow public routes to render without authentication
  if (isPublicRoute) {
    return <>{children}</>;
  }

  if (isRedirecting) {
    return (
      <div className="flex h-screen items-center justify-center">
        <Spinner aria-label="Redirecting..." />
      </div>
    );
  }

  if (!user || !user.role) {
    return (
      <div className="flex h-screen items-center justify-center">
        <Spinner aria-label="Loading..." />
      </div>
    );
  }

  if (isAccessDenied) {
    return <Navigate to="/404" replace />;
  }

  return (
    <>
      <SessionUpdate />
      <Inner user={user} setUser={setUser}>
        {children}
      </Inner>
    </>
  );
};

const Inner = ({
  user,
  setUser,
  children,
}: {
  user: AccessUser;
  setUser: (user: AccessUser) => void;
  children: ReactNode;
}) => {
  const hasPermission = useCallback(
    (...permissions: string[]) => {
      return access.hasPermission(user, ...permissions);
    },
    [user]
  );

  const hasAccess = useCallback(
    (accessKey: string) => {
      return access.hasAccess(user.confirmedAccess, accessKey);
    },
    [user]
  );

  const filterByPermissions = useCallback(
    <T,>(list: WithPermission<T>[]) => {
      return access.filterByPermissions(user, list);
    },
    [user]
  );

  const filterByRestrictions = useCallback(
    <T,>(list: WithRestriction<T>[]) => {
      return access.filterByRestrictions(user, list);
    },
    [user]
  );

  const foremanId = useMemo(() => {
    if (!user) {
      return undefined;
    }
    if (hasPermission(permissions.projects.view.asTechnician)) {
      return user.parentId ?? undefined;
    }
    if (hasPermission(permissions.projects.view.asForeman)) {
      return user.id;
    }
  }, [user]);

  const isReadOnlyJob = useCallback(
    (job: Pick<Job, 'foremanId'>) => {
      if (hasPermission(permissions.jobItems.update.splicingCcStatus)) return false;
      return !!foremanId && job.foremanId !== foremanId;
    },
    [foremanId, hasPermission]
  );

  // Returns false only when user has viewAll but the entity is outside their normal scope.
  /** VP scope — the user is one of the owners of the region the project definition belongs to. */
  const ownsRegionOf = useCallback(
    (definition: RegionOwnedDefinition | null | undefined) =>
      definition?.state?.market?.division?.owners?.some((o) => o.id === user.id) ?? false,
    [user]
  );

  const canActOnGig = useCallback(
    (gig: Pick<GigWithRelations, 'foremanId' | 'technicianId' | 'jobItem'>) => {
      if (!hasPermission(permissions.gigs.viewAll)) return true;

      const def = gig.jobItem?.job?.project?.definition;

      if (hasPermission(permissions.gigs.viewAsForeman)) return gig.foremanId === user.id;
      if (hasPermission(permissions.gigs.viewAsTechnician)) return gig.technicianId === user.id;
      if (hasPermission(permissions.gigs.viewAsOpm)) return def?.opms.some((o) => o.id === user.id) ?? false;
      if (hasPermission(permissions.gigs.viewAsCC)) return def?.coordinators.some((c) => c.id === user.id) ?? false;
      if (hasPermission(permissions.gigs.viewAsDirector)) return def?.directorId === user.id;
      if (hasPermission(permissions.gigs.viewAsVP)) return ownsRegionOf(def);
      // No specific role restriction (e.g. admin) — allow
      return true;
    },
    [user, hasPermission, ownsRegionOf]
  );

  const canActOnInvoice = useCallback(
    (invoice: Pick<CompanyInvoiceWithRelations, 'companyId' | 'project' | 'items'>) => {
      if (!hasPermission(permissions.companyInvoices.viewAll)) return true;
      const canViewAllSplicing = canBypassCompanyInvoiceRoleScopeForSplicing(user);
      const isSplicing = isSplicingInvoice({ items: invoice.items });

      if (canViewAllSplicing && isSplicing) return true;

      const opms = invoice.project?.definition?.opms ?? [];
      const isCompanyScoped = hasPermission(permissions.userProfile.companyScoped);
      const hasRoleRestriction = [UserRole.operationsProgramManager, UserRole.director, UserRole.vP].includes(
        user.roleId as UserRole
      );

      // Same as backend assertInvoiceScope: admin-level users are not scope-restricted.
      if (!isCompanyScoped && !hasRoleRestriction) return true;

      // Company scope is additive, not alternative.
      if (isCompanyScoped && invoice.companyId !== user.companyId) return false;

      switch (user.roleId) {
        case UserRole.operationsProgramManager:
          return opms.some((o) => o.id === user.id);
        case UserRole.director:
          return opms.some((o) => o.parent?.id === user.id);
        case UserRole.vP:
          return opms.some((o) => o.parent?.parent?.id === user.id);
        default:
          return true;
      }
    },
    [user, hasPermission]
  );

  const canActOnPerDiem = useCallback(
    (perDiem: Pick<PerDiemWithRelations, 'opm' | 'director'>) => {
      if (hasPermission(permissions.perDiem.viewAll)) return true;

      if (hasPermission(permissions.perDiem.view.asOpm)) return perDiem.opm.id === user.id;
      if (hasPermission(permissions.perDiem.view.asDirector)) return perDiem.director.id === user.id;
      return true;
    },
    [user, hasPermission]
  );

  const canActOnJobItem = useCallback(
    (jobItem: JobItemAccessTarget) => {
      // "My Crew" is only a map filter — fieldworkers still open other crews'
      // jobs, so crew assignment is enforced even without jobs.viewAll (UG-4741).
      if (foremanId && !hasPermission(permissions.jobItems.update.splicingCcStatus)) {
        return jobItem?.job?.foremanId === foremanId;
      }

      if (!hasPermission(permissions.jobs.viewAll)) return true;

      const definition = jobItem?.job?.project?.definition;

      if (hasPermission(permissions.projects.view.asTechnician)) return jobItem?.job?.foremanId === user.parentId;
      if (hasPermission(permissions.projects.view.asForeman)) return jobItem?.job?.foremanId === user.id;
      if (hasPermission(permissions.projects.view.asOpm))
        return definition?.opms?.some((o: { id: string }) => o.id === user.id) ?? false;
      if (hasPermission(permissions.projects.view.asCC))
        return definition?.coordinators?.some((c: { id: string }) => c.id === user.id) ?? false;
      if (hasPermission(permissions.projects.view.asDirector)) return definition?.directorId === user.id;
      if (hasPermission(permissions.projects.view.asVP)) return ownsRegionOf(definition);
      if (hasPermission(permissions.userProfile.companyScoped))
        return jobItem?.job?.foreman?.companyId === user.companyId;
      // No scope restriction (admin/COO/CEO) — allow
      return true;
    },
    [user, hasPermission, foremanId, ownsRegionOf]
  );

  const canActOnProject = useCallback(
    (project: ProjectAccessTarget) => {
      if (!hasPermission(permissions.projects.viewAll)) return true;

      const foremen = project?.definition?.foremen ?? [];

      if (hasPermission(permissions.projects.view.asTechnician))
        return (
          foremen.some((df) => df.foreman.id === user.parentId) &&
          (!project?.closeout || !['pendingCOPApproval', 'billingCompleted'].includes(String(project.closeout.status)))
        );
      if (hasPermission(permissions.projects.view.asForeman))
        return (
          foremen.some((df) => df.foreman.id === user.id) &&
          (!project?.closeout || !['pendingCOPApproval', 'billingCompleted'].includes(String(project.closeout.status)))
        );
      if (hasPermission(permissions.projects.view.asOpm))
        return project?.definition?.opms?.some((o: { id: string }) => o.id === user.id) ?? false;
      if (hasPermission(permissions.projects.view.asCC))
        return project?.definition?.coordinators?.some((c: { id: string }) => c.id === user.id) ?? false;
      if (hasPermission(permissions.projects.view.asDirector)) return project?.definition?.directorId === user.id;
      if (hasPermission(permissions.projects.view.asVP)) return ownsRegionOf(project?.definition);
      if (hasPermission(permissions.userProfile.companyScoped))
        return foremen.some((df) => df.foreman.companyId === user.companyId);
      // No scope restriction (admin/COO/CEO) — allow
      return true;
    },
    [user, hasPermission, ownsRegionOf]
  );

  const value = useMemo(
    () => ({
      user,
      setUser,
      hasAccess,
      hasPermission,
      filterByPermissions,
      filterByRestrictions,
      isReadOnlyJob,
      foremanId,
      canActOnGig,
      canActOnInvoice,
      canActOnPerDiem,
      canActOnJobItem,
      canActOnProject,
    }),
    [user]
  );

  return <AccessContext.Provider value={value}>{children}</AccessContext.Provider>;
};
